import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail, Wrench, Image as ImageIcon, FileText, ArrowUpRight,
  TrendingUp, Inbox, CheckCircle2, Clock,
} from "lucide-react";
import api from "../../lib/api";

const StatCard = ({ icon: Icon, label, value, hint, accent = "slate", testId }) => {
  const palette = {
    slate: "bg-slate-100 text-slate-700",
    orange: "bg-orange-100 text-orange-700",
    green: "bg-green-100 text-green-700",
    blue: "bg-blue-100 text-blue-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 admin-card-hover" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${palette[accent]}`}>
          <Icon size={18} />
        </div>
      </div>
      <div className="mt-4">
        <div className="text-3xl font-bold text-slate-900 leading-none">{value}</div>
        <div className="mt-1.5 text-sm text-slate-600">{label}</div>
        {hint && <div className="mt-0.5 text-xs text-slate-400">{hint}</div>}
      </div>
    </div>
  );
};

const STATUS_BADGE = {
  new: "admin-badge-new",
  read: "admin-badge-read",
  responded: "admin-badge-responded",
};

const AdminDashboard = () => {
  const [counts, setCounts] = useState({ inquiries: 0, services: 0, gallery: 0, newInquiries: 0, responded: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [inq, svc, gal] = await Promise.all([
          api.get("/admin/inquiries"),
          api.get("/admin/services"),
          api.get("/admin/gallery"),
        ]);
        setCounts({
          inquiries: inq.data.length,
          services: svc.data.length,
          gallery: gal.data.length,
          newInquiries: inq.data.filter((i) => i.status === "new").length,
          responded: inq.data.filter((i) => i.status === "responded").length,
        });
        setRecent(inq.data.slice(0, 5));
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Welcome back 👋</h1>
        <p className="mt-1 text-sm text-slate-500">Here's what's happening with RTC today.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Inbox} label="Total Inquiries" value={loading ? "—" : counts.inquiries} accent="slate" testId="tile-inquiries" />
        <StatCard icon={Clock} label="New / Unread" value={loading ? "—" : counts.newInquiries} accent="orange" hint={counts.newInquiries > 0 ? "Needs your attention" : "All caught up"} testId="tile-new-inquiries" />
        <StatCard icon={CheckCircle2} label="Responded" value={loading ? "—" : counts.responded} accent="green" testId="tile-responded" />
        <StatCard icon={TrendingUp} label="Services & Gallery" value={loading ? "—" : `${counts.services + counts.gallery}`} hint={`${counts.services} services · ${counts.gallery} images`} accent="blue" testId="tile-content" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent inquiries */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">Recent inquiries</h2>
              <p className="text-xs text-slate-500 mt-0.5">Latest 5 messages from the contact form</p>
            </div>
            <Link to="/admin/inquiries" className="text-xs font-semibold text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
              View all <ArrowUpRight size={12} />
            </Link>
          </div>
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-12 rounded-lg" />)}
            </div>
          ) : recent.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-100 text-slate-400 rounded-full mb-3">
                <Inbox size={20} />
              </div>
              <p className="text-sm text-slate-600">No inquiries yet</p>
              <p className="text-xs text-slate-400 mt-1">When visitors submit the contact form, they'll show up here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recent.map((i) => (
                <li key={i.id} className="px-6 py-3.5 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors" data-testid={`recent-${i.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 text-sm truncate">{i.name}</div>
                    <div className="text-xs text-slate-500 truncate">{i.email} · {i.service}</div>
                  </div>
                  <span className={`admin-badge ${STATUS_BADGE[i.status] || "admin-badge-read"}`}>{i.status}</span>
                  <span className="text-xs text-slate-400 hidden sm:inline whitespace-nowrap">
                    {new Date(i.created_at).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Quick actions */}
        <div className="space-y-4">
          <h2 className="font-semibold text-slate-900 px-1">Quick actions</h2>
          <Link to="/admin/services" className="group flex items-center justify-between bg-white border border-slate-200 rounded-xl p-4 admin-card-hover" data-testid="quick-action-services">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center group-hover:bg-orange-500 group-hover:text-white transition-colors">
                <Wrench size={18} />
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">Manage services</div>
                <div className="text-xs text-slate-500">Equipment & contracting</div>
              </div>
            </div>
            <ArrowUpRight size={16} className="text-slate-400 group-hover:text-slate-900" />
          </Link>
          <Link to="/admin/gallery" className="group flex items-center justify-between bg-white border border-slate-200 rounded-xl p-4 admin-card-hover" data-testid="quick-action-gallery">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-colors">
                <ImageIcon size={18} />
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">Update gallery</div>
                <div className="text-xs text-slate-500">{counts.gallery} images</div>
              </div>
            </div>
            <ArrowUpRight size={16} className="text-slate-400 group-hover:text-slate-900" />
          </Link>
          <Link to="/admin/content" className="group flex items-center justify-between bg-slate-900 text-white rounded-xl p-4 admin-card-hover" data-testid="quick-action-content">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 text-white rounded-lg flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                <FileText size={18} />
              </div>
              <div>
                <div className="font-semibold text-white text-sm">Edit site content</div>
                <div className="text-xs text-slate-400">Hero, About, Stats, Values</div>
              </div>
            </div>
            <ArrowUpRight size={16} className="text-slate-400 group-hover:text-orange-400" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
